/**
 * Utility functions for date and time formatting
 */

/**
 * Format a date string to a readable date format (without time)
 * @param dateString - ISO date string
 * @returns Formatted date string (e.g., "Jan 15, 2024")
 */
export const formatDate = (dateString?: string | null): string => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateString;
  }
};

/**
 * Format a date range for display (e.g. "Jan 20, 2026 — Jan 27, 2026")
 * @param startDate - ISO date string (YYYY-MM-DD)
 * @param endDate - ISO date string (YYYY-MM-DD)
 * @returns Formatted range string with em dash separator
 */
export const formatDateRange = (startDate?: string | null, endDate?: string | null): string => {
  if (!startDate || !endDate) return 'Select date range';
  try {
    const start = new Date(startDate + 'T12:00:00');
    const end = new Date(endDate + 'T12:00:00');
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return 'Select date range';
    const fmt = (d: Date) =>
      d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${fmt(start)} — ${fmt(end)}`;
  } catch {
    return 'Select date range';
  }
};

/**
 * Format a date string as MM/DD/YYYY (e.g. "02/06/2026")
 * @param dateString - ISO date string (YYYY-MM-DD)
 */
export const formatDateShort = (dateString?: string | null): string => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString + 'T12:00:00');
    if (isNaN(date.getTime())) return dateString;
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const y = date.getFullYear();
    return `${m}/${d}/${y}`;
  } catch {
    return dateString;
  }
};

/**
 * Format time only from an ISO date string
 */
export const formatTime = (dateString?: string | null): string => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return '';
  }
};

/**
 * Format a date string to include both date and time
 * @param dateString - ISO date string
 * @param includeTime - Whether to include time (default: true)
 * @param monthFormat - Format for month: 'short' or 'long' (default: 'short')
 * @returns Formatted date-time string
 */
export const formatDateTime = (
  dateString: string,
  includeTime: boolean = true,
  monthFormat: 'short' | 'long' = 'short'
): string => {
  try {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: monthFormat,
      day: 'numeric',
    };
    
    if (includeTime) {
      options.hour = 'numeric';
      options.minute = '2-digit';
      options.hour12 = true;
    }
    
    return date.toLocaleDateString('en-US', options);
  } catch {
    return dateString;
  }
};

/**
 * Format appointment time with optional duration
 * @param appointmentDate - ISO date string for appointment start
 * @param duration - Duration in minutes (optional)
 * @returns Formatted time string (e.g., "2:00 PM - 2:30 PM" or "2:00 PM")
 */
export const formatAppointmentTime = (appointmentDate: string, duration?: number): string => {
  try {
    const date = new Date(appointmentDate);
    const startTime = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    
    if (duration) {
      const endDate = new Date(date.getTime() + duration * 60000);
      const endTime = endDate.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
      return `${startTime} - ${endTime}`;
    }
    
    return startTime;
  } catch {
    return appointmentDate;
  }
};

/**
 * Format duration in minutes to a readable string
 * @param duration - Duration in minutes
 * @param format - Format style: 'min' or 'minutes' (default: 'min')
 * @returns Formatted duration string (e.g., "30 min" or "30 minutes")
 */
export const formatDuration = (duration?: number, format: 'min' | 'minutes' = 'min'): string => {
  if (!duration) return 'N/A';
  const suffix = format === 'minutes' ? ' minutes' : ' mins';
  return `${duration}${suffix}`;
};
